import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, RotateCcw, Trash2 } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import Button from "@/components/ui/Button";
import useGlobalStore from "@/store/globalStore";
import { DeletedItem, isAlreadyRestored } from "@/api/restore";
import useCampaignDetailStore from "../store/useCampaignDetailStore";

/**
 * "Recently deleted" — the campaign's deletions that are still inside their
 * 30-day retention window, each with a Restore button.
 *
 * Two deliberate behaviours:
 *
 *  - An empty list renders nothing at all. A campaign with no deletions is the
 *    normal case, and an empty-state card there is pure clutter.
 *  - Restore is confirmed, unlike the toast Undo. Restoring cancels the
 *    retention obligation, which breaks the download links in the export that
 *    was emailed when the delete happened. Immediately after a delete the user
 *    has that context in their head; days later, from this panel, they do not.
 */

const ROOT_TYPE_LABELS: Record<DeletedItem["root_type"], string> = {
  flight: "Flight",
  asset: "Asset",
  asset_version: "Asset version",
};

const formatDeletedAt = (value: string | null): string => {
  if (!value) return "Unknown date";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "Unknown date";
  return parsed.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatRemaining = (remainingDays: number | null): string => {
  if (remainingDays === null || remainingDays === undefined) return "";
  if (remainingDays <= 0) return "Last day to restore";
  return `${remainingDays} day${remainingDays === 1 ? "" : "s"} left`;
};

const formatScope = (count: number | null): string => {
  if (!count) return "";
  return `${count} record${count === 1 ? "" : "s"} come back`;
};

export default function RecentlyDeletedPanel({
  campaignId,
}: {
  campaignId: string;
}) {
  const {
    deletedItems,
    getDeletedItems,
    restoreFlight,
    restoreAsset,
    getCampaignDetailData,
  } = useCampaignDetailStore();
  const showSuccess = useGlobalStore((state) => state.showSuccess);
  const showInfo = useGlobalStore((state) => state.showInfo);
  const showError = useGlobalStore((state) => state.showError);

  const [isOpen, setIsOpen] = useState(false);
  const [pendingItem, setPendingItem] = useState<DeletedItem | null>(null);
  const [isRestoring, setIsRestoring] = useState(false);

  useEffect(() => {
    if (!campaignId) return;
    // A failure here leaves the list empty, which renders nothing — this panel
    // is supplementary, so it stays quiet rather than raising an error on a page
    // whose primary content loaded fine.
    void getDeletedItems(campaignId).catch(() => undefined);
  }, [campaignId, getDeletedItems]);

  // Only flights and assets have a restore endpoint. An asset_version deletion
  // is listed for visibility but cannot be restored from here.
  const isRestorableHere = (item: DeletedItem) =>
    item.restorable &&
    (item.root_type === "flight" || item.root_type === "asset");

  const blockedExplanation = (item: DeletedItem): string | null => {
    if (item.blocked_reason) return item.blocked_reason;
    if (item.root_type === "asset_version") {
      return "Restore this from the asset's version history.";
    }
    return null;
  };

  const handleConfirmRestore = async () => {
    if (!pendingItem || isRestoring) return;

    setIsRestoring(true);
    const label = ROOT_TYPE_LABELS[pendingItem.root_type];
    try {
      if (pendingItem.root_type === "flight") {
        await restoreFlight(pendingItem.root_id);
      } else {
        await restoreAsset(pendingItem.root_id);
      }
      showSuccess(`${label} restored.`);
      setPendingItem(null);
    } catch (error: any) {
      if (isAlreadyRestored(error)) {
        // Already back (e.g. restored in another tab, or via the toast undo).
        // Not a failure — the list just needs to catch up.
        showInfo(`That ${label.toLowerCase()} has already been restored.`);
        setPendingItem(null);
      } else {
        // Includes the 409 message naming the ancestor to restore first, which
        // is shown exactly as the server wrote it.
        showError(error?.message ?? `Failed to restore ${label.toLowerCase()}`);
      }
    } finally {
      setIsRestoring(false);
      // Refresh both the list and the campaign data so a restored row both
      // disappears from here and reappears in the views.
      await Promise.all([
        getDeletedItems(campaignId).catch(() => undefined),
        getCampaignDetailData(campaignId).catch(() => undefined),
      ]);
    }
  };

  if (!deletedItems.length) return null;

  return (
    <div className="mt-10 border border-gray-200 rounded-lg">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-expanded={isOpen}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
      >
        <span className="flex items-center gap-2">
          <Trash2 size={16} className="text-gray-500" />
          <span className="font-semibold text-sm">Recently deleted</span>
          <span className="text-xs text-gray-500">
            ({deletedItems.length})
          </span>
        </span>
        {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </button>

      {isOpen ? (
        <div className="border-t border-gray-200 divide-y divide-gray-100">
          <p className="px-4 py-3 text-xs text-gray-500">
            Deleted records and media are kept for 30 days, and an export of
            everything deleted was emailed to you.
          </p>
          {deletedItems.map((item) => {
            const canRestore = isRestorableHere(item);
            const explanation = canRestore ? null : blockedExplanation(item);

            return (
              <div
                key={item.event_id}
                className="px-4 py-3 flex items-start justify-between gap-4"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 text-xs">
                      {ROOT_TYPE_LABELS[item.root_type]}
                    </span>
                    <span className="text-sm font-medium truncate">
                      {item.root_label ?? "Untitled"}
                    </span>
                  </div>
                  <div className="mt-1 text-xs text-gray-500">
                    {[
                      `Deleted ${formatDeletedAt(item.deleted_at)}`,
                      formatRemaining(item.remaining_days),
                      formatScope(item.scope_item_count),
                    ]
                      .filter(Boolean)
                      .join(" · ")}
                  </div>
                  {explanation ? (
                    <div className="mt-1 text-xs text-amber-700">
                      {explanation}
                    </div>
                  ) : null}
                </div>

                <Button
                  type="secondary"
                  leadingIcon={<RotateCcw size={14} />}
                  disabled={!canRestore}
                  onClick={() => setPendingItem(item)}
                  className="shrink-0"
                >
                  Restore
                </Button>
              </div>
            );
          })}
        </div>
      ) : null}

      <Dialog
        open={!!pendingItem}
        onOpenChange={(next) => {
          if (isRestoring) return;
          if (!next) setPendingItem(null);
        }}
      >
        {/* `bg-white` is required: DialogContent defaults to `bg-background`,
            which Tailwind never generates here, so the panel would otherwise
            render transparent. */}
        <DialogContent
          showCloseButton={!isRestoring}
          className="sm:max-w-lg bg-white"
        >
          <DialogHeader>
            <DialogTitle>
              Restore{" "}
              {pendingItem
                ? ROOT_TYPE_LABELS[pendingItem.root_type].toLowerCase()
                : "item"}
              ?
            </DialogTitle>
            <DialogDescription>
              {pendingItem?.scope_item_count
                ? `This brings back ${pendingItem.root_label ?? "this item"} and ${
                    pendingItem.scope_item_count
                  } related record${
                    pendingItem.scope_item_count === 1 ? "" : "s"
                  }. `
                : `This brings back ${pendingItem?.root_label ?? "this item"}. `}
              Restoring also ends the 30-day retention hold, so the download
              links in the export that was emailed when it was deleted will stop
              working.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="justify-end gap-2 mt-2">
            <Button
              type="secondary"
              onClick={() => setPendingItem(null)}
              disabled={isRestoring}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              onClick={handleConfirmRestore}
              isLoading={isRestoring}
            >
              Restore
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
